import React, { useEffect, useMemo, useState } from 'react';
import { ProtectedRoute } from '../../../components/ProtectedRoute';
import { AsyncBoundary } from '../../../components/AsyncBoundary';
import {
  TierSummaryCard,
  StoreTieringTable,
  StorePagination,
  StoreDetailDrawer,
} from './components';
import {
  TierSummary,
  StoreItem,
  TierType,
  TrendDirection,
  ITEMS_PER_PAGE,
  tierFromPricing,
} from './storeTieringTypes';
import { useCustomersStore } from '../../../store/customersStore';
import { Customer } from '../../../services/customersApi';
import styles from './AdminStoreTiering.module.css';

function adapt(c: Customer): StoreItem {
  const tier: TierType = tierFromPricing(c.pricing_tier_name);
  const trend: TrendDirection = c.metrics.last_order_at ? 'up' : 'flat';
  const lastOrderDays = c.metrics.last_order_at
    ? Math.floor((Date.now() - new Date(c.metrics.last_order_at).getTime()) / (1000 * 60 * 60 * 24))
    : 999;
  return {
    id: String(c.id),
    storeName: c.store_name || c.full_name,
    city: [c.city, c.state].filter(Boolean).join(', ') || '—',
    tier,
    weeklyRevenue: Math.round(c.metrics.total_revenue / 4),
    target: tier === 'A' ? 1500 : tier === 'B' ? 800 : tier === 'C' ? 400 : null,
    trendPercent: 0,
    trendDirection: trend,
    margin: 0,
    frequency: c.metrics.order_count > 0 ? `${(c.metrics.order_count / 4).toFixed(1)}x` : '—',
    lastOrder: lastOrderDays < 999 ? `${lastOrderDays}d` : '—',
    lastOrderDays,
    fullAddress: c.address_line1 || '—',
    zipCode: c.zip_code || '—',
    route: '—',
    activeDays: '—',
    owner: c.full_name,
    phone: c.contact_phone || '—',
    email: c.email,
    salesRep: c.sales_rep_name || '—',
    ordersPerWeek: c.metrics.order_count > 0 ? +(c.metrics.order_count / 4).toFixed(1) : 0,
    aov: c.metrics.avg_order_value,
    tierMonths: 0,
    recommendedActions: [],
  };
}

const AdminStoreTiering: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedStore, setSelectedStore] = useState<StoreItem | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const customers = useCustomersStore((s) => s.items);
  const loading = useCustomersStore((s) => s.loading);
  const error = useCustomersStore((s) => s.error);
  const fetchCustomers = useCustomersStore((s) => s.fetchList);

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);

  const adapted = useMemo<StoreItem[]>(() => customers.map(adapt), [customers]);

  const tierSummaries = useMemo<TierSummary[]>(() => {
    const buckets: Record<TierType, StoreItem[]> = { A: [], B: [], C: [], D: [] };
    for (const s of adapted) buckets[s.tier].push(s);
    const description: Record<TierType, string> = {
      A: 'Premium pricing - Priority accounts',
      B: 'Standard pricing - Upsell targets',
      C: 'Discount pricing - Improve or drop',
      D: 'Wholesale pricing - Cut or cash & carry',
    };
    return (['A', 'B', 'C', 'D'] as TierType[]).map((tier) => ({
      tier,
      stores: buckets[tier].length,
      revenuePerWeek: `$${buckets[tier].reduce((sum, s) => sum + s.weeklyRevenue, 0).toLocaleString()}/week`,
      description: description[tier],
    }));
  }, [adapted]);

  const totalPages = Math.max(1, Math.ceil(adapted.length / ITEMS_PER_PAGE));
  const paginatedItems = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return adapted.slice(start, start + ITEMS_PER_PAGE);
  }, [adapted, currentPage]);

  return (
    <div className={styles.container}>
      <div className={styles.tiersGrid}>
        {tierSummaries.map((ts) => (
          <TierSummaryCard
            key={ts.tier}
            tier={ts.tier}
            stores={ts.stores}
            revenuePerWeek={ts.revenuePerWeek}
            description={ts.description}
          />
        ))}
      </div>

      <AsyncBoundary
        loading={loading && customers.length === 0}
        error={error}
        empty={!loading && adapted.length === 0}
        emptyMessage="No customers to tier yet"
        onRetry={() => fetchCustomers()}
      >
        <StoreTieringTable items={paginatedItems} onRowClick={(store) => { setSelectedStore(store); setDrawerOpen(true); }} />
        {totalPages > 1 && (
          <StorePagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        )}
      </AsyncBoundary>

      <StoreDetailDrawer store={selectedStore} open={drawerOpen} onClose={() => setDrawerOpen(false)} />
    </div>
  );
};

export const AdminStoreTieringPage: React.FC = () => (
  <ProtectedRoute requiredRoles={['admin']}>
    <AdminStoreTiering />
  </ProtectedRoute>
);

export { AdminStoreTiering };
export default AdminStoreTiering;
